import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  dependentsQuestion,
  claimCreditQuestions,
  otherCreditsQuestion,
  itemizedDeductionQuestions,
  coveredEmployeeQuestion,
  additionalWithholdingQuestions,
  totalAllowancesQuestions,
  getResidencyQuestions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import { getUpdatedQuestions } from '../helpers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function NewYorkForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  resetInitialQuestions,
  formLink,
  version,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const residencyForm = useRef();
  const residencyQuestions = getResidencyQuestions(version);
  const residencyInitQ = getInitialQuestions(residencyQuestions);
  const [residency, setResidency] = useState(residencyInitQ);

  useEffect(() => {
    setResidency(residencyInitQ);
  }, [version]);

  const dependentsForm = useRef();
  const dependentsInitQ = getInitialQuestions(dependentsQuestion);
  const [dependents, setDependents] = useState(dependentsInitQ);

  const claimCreditInitQ = getInitialQuestions(claimCreditQuestions);
  const [claimCredits, setClaimCredits] = useState(claimCreditInitQ);

  const creditsForm = useRef();
  const otherCreditsInitQ = getInitialQuestions(otherCreditsQuestion);
  const [otherCredits, setOtherCredits] = useState(otherCreditsInitQ);

  const itemizedDeductionsForm = useRef();
  const [itemizeDeductions, setItemizeDeductions] = useState(
    data.itemized_deductions || false,
  );
  const itemizeDeductionsInitQ = getInitialQuestions(
    itemizedDeductionQuestions,
  );
  const [itemizeDeductionsQs, setItemizeDeductionsQs] = useState(
    itemizeDeductionsInitQ,
  );

  const [coveredEmployee, setCoveredEmployee] = useState(
    data.covered_employee || false,
  );
  const coveredEmployeeInitQ = getInitialQuestions(coveredEmployeeQuestion);
  const [coveredEmployeeQ, setCoveredEmployeeQ] = useState(coveredEmployeeInitQ);

  const additionalWithholdingsForm = useRef();
  const additionalWithholdingInitQ = getInitialQuestions(
    additionalWithholdingQuestions,
  );
  const [additionalWithholdings, setAdditionalWithholdings] = useState(
    additionalWithholdingInitQ,
  );

  const totalAllowancesForm = useRef();
  const [totalAllowances, setTotalAllowances] = useState(
    getInitialQuestions(totalAllowancesQuestions),
  );

  const is2023OrNewer = !['2021', '2022'].includes(version);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={(props) => {
          setBasicInfo(getUpdatedQuestions(props));
        }}
        ref={basicInfoForm}
      />
      <FormConsumer
        questions={residency}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={(props) => {
          setResidency(getUpdatedQuestions(props));
        }}
        ref={residencyForm}
      />
      {!is2023OrNewer && renderDependents(isReviewStep)}
      {is2023OrNewer && renderTotalAllowances(isReviewStep)}
    </Flex>
  );

  const renderDependents = (isReviewStep = false) => (
    <FormConsumer
      questions={dependents}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={(props) => {
        setDependents(getUpdatedQuestions(props));
      }}
      ref={dependentsForm}
    />
  );

  const renderCreditsForm = (isReviewStep) => {
    if (is2023OrNewer) return null;

    return (
      <Flex column>
        <Text mb="10px" style={{ fontWeight: '600', fontSize: '14px' }}>
          Withholding allowances for New York State and Yonkers
        </Text>
        <FormConsumer
          questions={claimCredits}
          {...(isReviewStep && {
            viewResultAsManager: true,
            reviewActions: [],
          })}
          onQuestionChange={(props) => {
            setClaimCredits(getUpdatedQuestions(props));
          }}
        />
        <FormConsumer
          questions={otherCredits}
          {...(isReviewStep && {
            viewResultAsManager: true,
            reviewActions: [],
          })}
          onQuestionChange={(props) => {
            setOtherCredits(getUpdatedQuestions(props));
          }}
          ref={creditsForm}
        />
      </Flex>
    );
  };

  const renderItemizedDeductionQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={itemizeDeductionsQs}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={(props) => {
          setItemizeDeductionsQs(getUpdatedQuestions(props));
        }}
        ref={itemizedDeductionsForm}
      />
    </Flex>
  );

  const renderCoveredEmployeeQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={coveredEmployeeQ}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={(props) => {
          setCoveredEmployeeQ(getUpdatedQuestions(props));
        }}
        ref={creditsForm}
      />
    </Flex>
  );

  const renderDeductions = (isReviewStep) => (
    <Flex column>
      {!is2023OrNewer && (
        <>
          <S.QuestionWrapper mb="20px">
            <label>
              <Flex>
                <S.Input
                  type="checkbox"
                  value={itemizeDeductions}
                  checked={itemizeDeductions}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setItemizeDeductions(e.target.checked);
                  }}
                />
                <Box ml={2} />
                Check if you expect to itemize deductions on your state tax
                return
              </Flex>
            </label>
          </S.QuestionWrapper>
          {itemizeDeductions && renderItemizedDeductionQuestions(isReviewStep)}
          <S.QuestionWrapper mb="20px">
            <label>
              <Flex>
                <S.Input
                  type="checkbox"
                  value={coveredEmployee}
                  checked={coveredEmployee}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setCoveredEmployee(e.target.checked);
                  }}
                />
                <Box ml={2} />
                Check if you expect to be a covered employee of an employer who
                elected to pay the employer compensation expense tax in 2021
              </Flex>
            </label>
          </S.QuestionWrapper>
          {coveredEmployee && renderCoveredEmployeeQuestions(isReviewStep)}
        </>
      )}
      {renderAdditionalWithholdings(isReviewStep)}
    </Flex>
  );

  const renderAdditionalWithholdings = (isReviewStep = false) => (
    <>
      <Text style={{ fontWeight: '600' }} mb={2}>
        Enter any additional withholdings per pay period under special
        agreement with your employer
      </Text>
      <FormConsumer
        questions={additionalWithholdings}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={(props) => {
          setAdditionalWithholdings(getUpdatedQuestions(props));
        }}
        ref={additionalWithholdingsForm}
      />
    </>
  );

  const renderTotalAllowances = (isReviewStep = false) => (
    <FormConsumer
      questions={totalAllowances}
      {...(isReviewStep && {
        viewResultAsManager: true,
        reviewActions: [],
      })}
      onQuestionChange={(props) => {
        setTotalAllowances(getUpdatedQuestions(props));
      }}
      ref={totalAllowancesForm}
    />
  );

  const [nycAllowances, setNycAllowances] = useState(
    data.claim_nyc_allowances || false,
  );

  const renderNYCAllowances = (isReviewStep) => {
    if (is2023OrNewer) return null;

    return (
      <Flex column>
        <Text mb="10px" style={{ fontWeight: '600', fontSize: '14px' }}>
          Withholding allowances for New York City
        </Text>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex>
              <S.Input
                type="checkbox"
                value={nycAllowances}
                checked={nycAllowances}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setNycAllowances(e.target.checked);
                }}
              />
              <Box ml={2} />
              Check if you want to calculate and claim any allowances for New
              York City.
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    );
  };

  const getStandardDeductionVal = () => {
    const fs = itemizeDeductionsQs[1].answer.answer.status_amt || '';
    let deduction = 0;
    if (fs !== '') {
      switch (fs) {
        case 'single_not_dependent':
          deduction = 8000;
          break;
        case 'single_dependent':
          deduction = 3100;
          break;
        case 'head_of_household_amt':
          deduction = 11200;
          break;
        case 'widow':
          deduction = 16050;
          break;
        case 'married_joint':
          deduction = 16050;
          break;
      }
    }
    return deduction;
  };

  const calculatePart2 = () => {
    const res = [];
    const line20 = parseInt(
      itemizeDeductionsQs[0].answer.answer.itemized_deductions_value,
    ) || 0;
    const line21 = getStandardDeductionVal();
    res.push(line21);

    const line22 = line21 > line20 ? 0 : line20 - line21;
    res.push(line22);

    const line23 = Math.trunc(line22 / 1000);
    res.push(line23);

    return res;
  };

  const calculatePart3 = () => {
    const res = [];

    const line24 = parseInt(coveredEmployeeQ[0].answer.answer.expected_wages);

    const line25Temp = line24 - 40000;
    const line25 = line25Temp <= 0 ? 0 : line25Temp;
    res.push(line25);

    const line26 = line25 * 0.05;
    res.push(line26);

    const line27 = line26 * 0.935;
    res.push(line27);

    const line28 = line27 / 65;
    res.push(Math.trunc(line28));

    return res;
  };

  const calculatePart1 = () => {
    let res = 0;
    const arr = [];

    // Part 1, Line 6:
    res += parseInt(dependents[0].answer.answer.num_dependents) || 0;

    // Part 1, Lines 7-9:
    if (
      claimCredits[0].answer.answer.credit_claimed.includes('college_tuition')
    ) res += 1;
    if (claimCredits[0].answer.answer.credit_claimed.includes('nys_household')) res += 1;
    if (claimCredits[0].answer.answer.credit_claimed.includes('property_tax')) res += 1;

    // Part 1, Lines 10-12:
    if (
      claimCredits[0].answer.answer.credit_claimed.includes(
        'child_dependent_care',
      )
    ) res += 3;
    if (claimCredits[0].answer.answer.credit_claimed.includes('earned_income')) res += 3;
    if (
      claimCredits[0].answer.answer.credit_claimed.includes(
        'empire_state_child',
      )
    ) res += 3;

    // Part 1, Line 13:
    if (claimCredits[0].answer.answer.credit_claimed.includes('nyc_school_tax')) res += 2;

    // Part 1, Line 14:
    res += parseInt(otherCredits[0].answer.answer.other_credits) || 0;

    // Part 1, Line 15:
    if (
      claimCredits[0].answer.answer.credit_claimed.includes(
        'head_of_household_status',
      )
    ) res += 2;

    // Part 1, Line 16:
    const line16 = Math.trunc(
      parseInt(otherCredits[1].answer.answer.federal_adjustments) / 1000,
    ) || 0;
    res += line16;
    arr.push(line16);

    // Part 1, Line 17:
    const coveredEmployeeVal = coveredEmployee ? calculatePart3()[3] : 0;
    arr.push(coveredEmployeeVal);
    res += coveredEmployeeVal;

    // Part 1, Line 18:
    const itemizeDeductionsVal = itemizeDeductions ? calculatePart2()[2] : 0;
    res += itemizeDeductionsVal;

    arr.push(res);
    return arr;
  };

  const calculatePart4 = () => {
    const res = [];
    let num = 0;
    if (
      claimCredits[0].answer.answer.credit_claimed.includes(
        'head_of_household_status',
      )
    ) num += 2;
    num
      += Math.trunc(
        parseInt(otherCredits[1].answer.answer.federal_adjustments) / 1000,
      ) || 0;

    const coveredEmployeeVal = coveredEmployee ? calculatePart3()[3] : 0;
    num += coveredEmployeeVal;

    const itemizeDeductionsVal = itemizeDeductions ? calculatePart2()[2] : 0;
    num += itemizeDeductionsVal;

    res.push(num);

    res.push(num + (parseInt(dependents[0].answer.answer.num_dependents) || 0));

    return res;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderCreditsForm()}
          {renderDeductions()}
          {renderNYCAllowances()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            const formRefs = [
              basicInfoForm,
              residencyForm,
              dependentsForm,
              itemizedDeductionsForm,
              creditsForm,
              additionalWithholdingsForm,
              totalAllowancesForm,
            ];
            const hasError = formRefs.some((formRef) => {
              const form = formRef.current;
              if (!form) return;

              return form.validate();
            });

            if (hasError) return;

            [].reduce;

            const accumulateMappingFields = (acc, q) => ({ ...acc, ...q.answer.answer });

            const basicInfoData = basicInfo.reduce(accumulateMappingFields, {});
            const residencyData = residency.reduce(accumulateMappingFields, {});
            const dependentsData = dependents.reduce(
              accumulateMappingFields,
              {},
            );
            const claimCreditsData = claimCredits.reduce(
              accumulateMappingFields,
              {},
            );
            const otherCreditsData = otherCredits.reduce(
              accumulateMappingFields,
              {},
            );
            const additionalWithholdingData = additionalWithholdings.reduce(
              accumulateMappingFields,
              {},
            );
            const totalNumAllowancesData = totalAllowances.reduce(
              accumulateMappingFields,
              {},
            );

            let itemizeDeductionsTemp = false;
            let itemizeDeductionsData;

            let coveredEmployeeTemp = false;
            let coveredEmployeeData = '';

            let claimNYCTemp = false;

            if (itemizeDeductions) {
              itemizeDeductionsTemp = itemizeDeductions;
              itemizeDeductionsData = itemizeDeductionsQs.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
            } else {
              itemizeDeductionsData = resetInitialQuestions(
                itemizedDeductionQuestions,
              ).reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
            }

            if (coveredEmployee) {
              coveredEmployeeTemp = coveredEmployee;
              coveredEmployeeData = coveredEmployeeQ.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
            } else {
              coveredEmployeeData = resetInitialQuestions(
                coveredEmployeeQuestion,
              ).reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
            }

            if (nycAllowances) {
              claimNYCTemp = nycAllowances;
            }

            const pt1Calculations = calculatePart1();
            const pt2Calculations = calculatePart2();
            const pt3Calculations = calculatePart3();
            const pt4Calculations = calculatePart4();

            const allCalculationData = {
              part_1_line_16: pt1Calculations[0].toString() || '',
              part_1_line_17: pt1Calculations[1].toString() || '',
              total_num_allowances_nys_yonkers:
                pt1Calculations[2].toString() || '',
              part_2_line_21: itemizeDeductions
                ? pt2Calculations[0].toString()
                : '',
              part_2_line_22: itemizeDeductions
                ? pt2Calculations[1].toString()
                : '',
              part_2_line_23: itemizeDeductions
                ? pt2Calculations[2].toString()
                : '',
              part_3_line_25: coveredEmployee
                ? pt3Calculations[0].toString()
                : '',
              part_3_line_26: coveredEmployee
                ? pt3Calculations[1].toString()
                : '',
              part_3_line_27: coveredEmployee
                ? pt3Calculations[2].toString()
                : '',
              part_3_line_28: coveredEmployee
                ? pt3Calculations[3].toString()
                : '',
              part_4_line_30: nycAllowances
                ? pt4Calculations[0].toString()
                : '',
              part_4_line_31: nycAllowances
                ? pt4Calculations[1].toString()
                : '',
            };

            const checkboxData = {
              claim_nyc_allowances: claimNYCTemp,
              itemized_deductions: itemizeDeductionsTemp,
              covered_employee: coveredEmployeeTemp,
            };
            const allCreditsData = {
              ...claimCreditsData,
              ...residencyData,
              ...dependentsData,
              ...otherCreditsData,
              ...additionalWithholdingData,
            };
            const data = {
              ...basicInfoData,
              ...allCreditsData,
              ...allCalculationData,
              ...itemizeDeductionsData,
              ...coveredEmployeeData,
              ...checkboxData,
              ...totalNumAllowancesData,
            };

            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderCreditsForm(true)}
        {renderDeductions(true)}
        {renderNYCAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default NewYorkForm;
